import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle, Send, Info, FileWarning } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";

interface ModalProps {
  purchaseOrderId: string;
  onClose?: () => void;
}

interface SuccessProps extends ModalProps {}

interface ActionRequiredProps extends ModalProps {}

interface ActionRequiredPortalOrderProps extends ActionRequiredProps {
  purchaseOrderAsn?: string;
}

interface ErrorProps extends ModalProps {
  onRetry: () => void;
}

interface PreviewErrorProps extends ErrorProps {
  onClose: () => void;
}

const Success: React.FC<SuccessProps> = ({ purchaseOrderId, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Purchase Order Sent</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              The vendor has been emailed the purchase order{" "}
              <span className="font-semibold text-success-700">(ID: {purchaseOrderId})</span>
            </p>
          </div>
          <p className="text-sm text-gray-600 text-center">Keep an eye out for vendor updates</p>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const SuccessTBC: React.FC<SuccessProps> = ({ purchaseOrderId, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Purchase Order TBC</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              The vendor has been emailed the purchase order TBC prompt{" "}
              <span className="font-semibold text-success-700">(ID: {purchaseOrderId})</span>
            </p>
          </div>
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-primary-600 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-sm text-primary-700">
                This vendor has portal access and should confirm the order soon
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-600 text-center">Keep an eye out for vendor updates</p>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const SuccessCollectionOrder: React.FC<SuccessProps> = ({ purchaseOrderId, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Purchase Order Sent</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              The vendor has been emailed the purchase order{" "}
              <span className="font-semibold text-success-700">(ID: {purchaseOrderId})</span>
            </p>
          </div>
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-warning-600 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-sm text-warning-700">This vendor processes orders via arranged collection</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 text-center">Keep an eye out for vendor updates</p>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActionRequiredPortalOrder: React.FC<ActionRequiredPortalOrderProps> = ({
  purchaseOrderId,
  purchaseOrderAsn,
  onClose
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-warning-600" />
            <h2 className="text-lg font-semibold text-gray-900">Action Required</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          {/* Warning Message */}
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-warning-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-warning-800 mb-1">Vendor uses external portal</p>
                <p className="text-sm text-warning-700">
                  The purchase order PDF has been sent to the orders inbox{" "}
                  <span className="font-semibold">(ID: {purchaseOrderId})</span>
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-primary-800 mb-2 flex items-center">
              <Send className="w-4 h-4 mr-2" />
              Next Steps
            </h4>
            <ul className="text-sm text-primary-700 space-y-1 ml-6 list-disc">
              <li>Submit the purchase order PDF to the vendor portal</li>
            </ul>
          </div>

          {/* ASN Information */}
          {purchaseOrderAsn && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">ASN:</span> {purchaseOrderAsn}
              </p>
            </div>
          )}

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActionRequiredPortalAndCollectionOrder: React.FC<ActionRequiredPortalOrderProps> = ({
  purchaseOrderId,
  purchaseOrderAsn,
  onClose
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-warning-600" />
            <h2 className="text-lg font-semibold text-gray-900">Action Required</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          {/* Warning Message */}
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-warning-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-warning-800 mb-1">Vendor uses external portal</p>
                <p className="text-sm text-warning-700">
                  The purchase order PDF has been sent to the orders inbox{" "}
                  <span className="font-semibold">({purchaseOrderId})</span>
                </p>
                <p className="text-sm text-warning-700 mt-2">
                  Once confirmed, this vendor processes orders via arranged collection
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-primary-800 mb-2 flex items-center">
              <Send className="w-4 h-4 mr-2" />
              Next Steps
            </h4>
            <ul className="text-sm text-primary-700 space-y-1 ml-6 list-disc">
              <li>Submit the purchase order PDF to the vendor portal</li>
            </ul>
          </div>

          {/* ASN Information */}
          {purchaseOrderAsn && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">ASN:</span> {purchaseOrderAsn}
              </p>
            </div>
          )}

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const SuccessTBCCollectionOrder: React.FC<SuccessProps> = ({ purchaseOrderId, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Purchase Order TBC</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              The vendor has been emailed the purchase order{" "}
              <span className="font-semibold text-success-700">(ID: {purchaseOrderId})</span>
            </p>
          </div>
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-warning-600 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-sm text-warning-700">
                Once confirmed, this vendor processes orders via arranged collection
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-600 text-center">Keep an eye out for vendor updates</p>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const SendError: React.FC<ErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Send Failed</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5">
          <p className="text-sm text-gray-700 text-center mb-6">
            An error occurred while sending the purchase order. Please try again.
          </p>

          {/* Footer Button */}
          <div className="flex justify-center">
            <button
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onRetry}
            >
              Retry Sending
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviewError: React.FC<PreviewErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <FileWarning className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Preview Failed</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            &times;
          </button>
        </div>

        <div className="p-5">
          <p className="text-sm text-gray-700 text-center mb-6">
            Failed to load the purchase order preview. Please try again.
          </p>

          {/* Footer Buttons */}
          <div className="flex justify-center space-x-3">
            <button
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
              onClick={onClose}
            >
              Close
            </button>
            <button
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onRetry}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SendPurchaseOrderModals = {
  Success,
  SuccessTBC,
  SuccessCollectionOrder,
  ActionRequiredPortalOrder,
  ActionRequiredPortalAndCollectionOrder,
  SuccessTBCCollectionOrder,
  SendError,
  PreviewError
};

export { SendPurchaseOrderModals };
